import { Component, OnInit, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from "@angular/core";
import { ModalDialogParams } from "../../../modal/modals/modal.dialog.component";
import { Table } from "../../../s25-table/Table";
import { Bind } from "../../../../decorators/bind.decorator";
import { BpeService, EmailTemplate } from "../../../bpe/bpe.service";
import { CacheRepository } from "../../../../decorators/cache.decorator";
import { GenericTableButtonComponent } from "../../../s25-table/generics/generic.table.button.component";
import { ModalService } from "../../../modal/modal.service";
import { S25TableComponent } from "../../../s25-table/s25.table.component";
import { BpeUtil } from "../../../bpe/bpe.util";
import { S25Util } from "../../../../util/s25-util";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { S25MdTagMultiSelectComponent } from "../../../master-definitions/s25.md.tag.picker.component";
import { MultiselectModelI } from "../../../s25-multiselect/s25.multiselect.component";
import { MasterDefinitionTagsService } from "../../../../services/master.definitions/master.definition.tags.service";
import { MasterDefTag } from "../../../../pojo/MasterDefTag";
import { GenericTableListComponent } from "../../../s25-table/generics/generic.table.list.component";

@TypeManagerDecorator("s25-ng-email-template-list")
@Component({
    selector: "s25-ng-email-template-list",
    template: `
        <h2 class="c-margin-bottom--single">Template Settings</h2>
        @if (isInit) {
            <div class="top">
                <div class="header">
                    <button class="aw-button aw-button--primary" (click)="onCreateClick()">Add Template</button>
                    <label>
                        Preview reference:
                        <input type="text" [(ngModel)]="eventReference" class="c-input" placeholder="YYYY-ABCDEF" />
                    </label>
                </div>
                <s25-ng-table
                    [dataSource]="tableData"
                    [hasRefresh]="true"
                    [hasFilter]="true"
                    [columnSortable]="true"
                    [pivotThreshold]="800"
                    [unlimitedWidth]="true"
                ></s25-ng-table>
            </div>
        }
    `,
    styles: `
        .header {
            display: flex;
            justify-content: space-between;
        }

        .header > button {
            margin-bottom: 1em;
        }
    `,
    encapsulation: ViewEncapsulation.Emulated,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class S25EmailTemplateListComponent implements OnInit {
    @ViewChild(S25TableComponent) tableComponent: S25TableComponent;

    isInit: boolean = false;
    tableData: Table.DataSource;
    templates = new Map<number, EmailTemplate>();
    eventReference: string = "";

    ngOnInit() {
        this.tableData = {
            type: "unpaginated",
            dataSource: this.getRows,
            columns: [
                {
                    id: "name",
                    header: "Name",
                },
                {
                    id: "manual",
                    header: "Manual",
                    width: 70,
                },
                {
                    id: "iCalFile",
                    header: "iCalFile",
                    width: 70,
                },
                {
                    id: "to",
                    header: "To",
                    maxWidth: 400,
                },
                S25MdTagMultiSelectComponent.Column(120),
                GenericTableButtonComponent.Column("Preview", this.onPreviewClick, "outline"),
                GenericTableButtonComponent.Column("Copy", this.onCopyClick, "outline"),
                GenericTableButtonComponent.Column("Edit", this.onEditClick, "outline"),
                GenericTableButtonComponent.Column("Delete", this.onDeleteClick, "danger--outline"),
            ],
        };
        this.isInit = true;
    }

    @Bind
    async getRows(query: Table.UnpaginatedQuery): Promise<Table.DataSourceResponse> {
        if (query.forceRefresh) CacheRepository.invalidateByService("BpeService");
        const templates = await BpeService.getFullTemplates();
        for (const template of templates) {
            this.templates.set(template.itemId, template);
        }

        return {
            rows: templates.map(this.mapToRow),
        };
    }

    onCreateClick() {
        const template: Omit<EmailTemplate, "itemId"> = {
            comment: "",
            mode: "form",
            cc: "",
            reports: "",
            itemName: "New Email Template",
            bcc: "",
            code: "",
            subject: "",
            isManual: 0,
            iCalFile: 0,
            to: "",
            body: "",
        };
        ModalService.modal("edit-email-template", {
            template: <EmailTemplate>template,
            title: `Create New  Template`,
            onSave: this.tableComponent.refresh,
            reference: this.eventReference,
        });
    }

    @Bind
    onPreviewClick(row: Table.Row) {
        if (!this.eventReference) return alert("Please provide an event reference for the preview.");

        const template = this.templates.get(row.id as number);
        const code = template.mode === "form" ? BpeUtil.templateToCode(template) : template.code;
        ModalService.modal("email-template-preview", {
            name: template.itemName,
            code,
            reference: this.eventReference,
            isToDoTemplate: template.isToDoTemplate,
        });
    }

    @Bind
    onCopyClick(row: Table.Row) {
        const template: Partial<EmailTemplate> = S25Util.deepCopy(this.templates.get(row.id as number));
        delete template.itemId; // Omit id to create as new
        template.itemName += " copy";

        ModalService.modal("edit-email-template", {
            template: <EmailTemplate>template,
            title: `Copy Template: ${row.name}`,
            onSave: this.tableComponent.refresh,
            reference: this.eventReference,
        });
    }

    @Bind
    onEditClick(row: Table.Row) {
        ModalService.modal("edit-email-template", {
            template: this.templates.get(row.id as number),
            title: `Edit Template: ${row.name}`,
            onSave: this.tableComponent.refresh,
            reference: this.eventReference,
        });
    }

    @Bind
    async onDeleteClick(row: Table.Row) {
        const dialogData = ModalService.dialogType(
            "Yes No",
            {
                message: "Are you sure you want to delete this email template?",
                title: "Confirm Deletion",
            },
            "No",
        );
        await ModalService.modal("dialog", dialogData);
        if (dialogData.answer !== ModalDialogParams.Answer.Yes) return;

        await BpeService.deleteTemplate(row.id as number).catch(this.error);
        this.tableComponent.refresh();
    }

    @Bind
    onTagsUpdate(tagsModel: MultiselectModelI, templateId: number) {
        const added = tagsModel.addedItems?.map((items) => items.itemId);
        const removed = tagsModel.removedItems?.map((items) => items.itemId);

        if (!added?.length && !removed?.length) return;

        return MasterDefinitionTagsService.editTagsOnObject(
            MasterDefTag.types.email_template,
            null,
            templateId,
            added as number[],
            removed as number[],
        );
    }

    @Bind
    mapToRow(item: EmailTemplate): Table.Row {
        return {
            id: item.itemId,
            name: String(item.itemName),
            cells: {
                name: {
                    text: item.itemName,
                },
                manual: {
                    text: item.isManual ? "Yes" : "No",
                },
                iCalFile: {
                    text: item.iCalFile ? "Yes" : "No",
                },
                to: {
                    component: GenericTableListComponent,
                    inputs: {
                        items: item.to.split(",").map((address) => ({ text: address.trim() })),
                    },
                },
                tags: {
                    inputs: {
                        selectedItems: S25Util.array
                            .forceArray(item.tags)
                            .map((tag) => ({ itemId: tag.tagId, itemName: tag.tagName })),
                        selectBean: { hasCommit: true },
                    },
                    outputs: {
                        selectedItemsChange: (selectBean) => this.onTagsUpdate(selectBean, item.itemId),
                    },
                },
            },
        };
    }

    error(error: any) {
        S25Util.showError(error);
    }
}
